import { cva } from 'class-variance-authority';
import { focusRing } from 'components/styles/focusRing';

/**
 * File upload class recipes. Slots live on `.ds-file-upload` in `design-system.css`; every one names
 * a semantic role, so there is no `dark:` variant anywhere here.
 *
 * Ported from Figma `File Upload` 1993:129164 (Layout Card/Row/Tile x State Default/Hover/Focus/
 * Active x hasError x isDisabled, 18 cells), `File Upload Item` 1993:129222 (Status, with a Preview
 * slot) and `File Preview` 1993:128891, read 2026-09-25.
 *
 * ── State reading ─────────────────────────────────────────────────────────────────────────────
 *
 *   Figma      browser                                   selector
 *   Default    resting                                   —
 *   Hover      pointer over the zone                     `hover:`, not while disabled or dragging
 *   Focus      keyboard focus on Browse (card, row)      `has-[[data-part=browse]:focus-visible]`
 *              keyboard focus on the tile itself         `focus-visible` (the tile is the button)
 *   Active     a file held over the zone                 `data-dragging`, written from drag events
 *   hasError   a rejected file or validation message     `data-invalid`
 *   isDisabled disabled                                  `data-disabled`
 *
 * Figma's `Active` is drag-over, which is the one fully accent state. The Focus cell draws the halo
 * on the ZONE, so on card and row the ring moves off the Browse button onto the zone around it —
 * one ring, not two.
 */

/** The shared halo, written against the Browse button inside the zone rather than the zone itself. */
const zoneHalo =
  'has-[[data-part=browse]:focus-visible]:shadow-[var(--effect-focus-inner-x)_var(--effect-focus-inner-y)_var(--effect-focus-inner-blur)_var(--effect-focus-inner-spread)_var(--effect-focus-inner),var(--effect-focus-outer-x)_var(--effect-focus-outer-y)_var(--effect-focus-outer-blur)_var(--effect-focus-outer-spread)_var(--effect-focus-outer)]';

export const fileUploadRootRecipe = cva(
  [
    'ds-file-upload group/fu relative rounded-md border border-dashed font-sans',
    'bg-[var(--ds-file-upload-bg)] border-[var(--ds-file-upload-border)]',
    'not-data-[disabled]:not-data-[dragging]:hover:bg-[var(--ds-file-upload-bg-hover)]',
    'not-data-[disabled]:not-data-[dragging]:not-data-[invalid]:hover:border-[var(--ds-file-upload-border-hover)]',
    'data-[invalid]:border-[var(--ds-file-upload-border-invalid)]',
    // Drag-over is the one solid rim: the dashed edge says "a place to drop", the solid one "here".
    'data-[dragging]:border-solid data-[dragging]:bg-[var(--ds-file-upload-bg-dragging)] data-[dragging]:border-[var(--ds-file-upload-border-dragging)]',
    'data-[disabled]:cursor-not-allowed data-[disabled]:bg-[var(--ds-file-upload-bg-disabled)] data-[disabled]:border-[var(--ds-file-upload-border-disabled)]',
    'transition-[background-color,border-color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
  ],
  {
    variants: {
      layout: {
        card: ['flex w-full flex-col items-center gap-[12px] p-[32px] text-center', 'cursor-pointer', zoneHalo],
        row: ['flex w-full items-center gap-[12px] px-[16px] py-[12px] text-left', 'cursor-pointer', zoneHalo],
        tile: [
          'flex h-[72px] w-[128px] flex-col items-center justify-center gap-[4px] p-[8px]',
          'cursor-pointer outline-none',
          focusRing('self'),
        ],
      },
    },
    defaultVariants: { layout: 'card' },
  }
);

/** The 40px disc behind the arrow. Accent-muted at rest: the one hint of colour before a drag. */
export const fileUploadBadgeRecipe = cva([
  'flex size-[40px] shrink-0 items-center justify-center rounded-full',
  'bg-[var(--ds-file-upload-badge)] text-[var(--ds-file-upload-icon)]',
  'group-data-[dragging]/fu:bg-[var(--ds-file-upload-badge-dragging)]',
  'group-data-[disabled]/fu:bg-[var(--ds-file-upload-badge-disabled)] group-data-[disabled]/fu:text-[var(--ds-file-upload-disabled)]',
]);

/** The tile's plus, the same accent as the zone's arrow. */
export const fileUploadTileIconRecipe = cva([
  'text-[var(--ds-file-upload-icon)]',
  'group-data-[disabled]/fu:text-[var(--ds-file-upload-disabled)]',
]);

export const fileUploadTextRecipe = cva(['flex min-w-0 flex-col gap-[4px]'], {
  variants: {
    layout: { card: 'items-center', row: 'flex-1 items-start' },
  },
  defaultVariants: { layout: 'card' },
});

export const fileUploadTitleRowRecipe = cva(['inline-flex items-center gap-[4px]']);

export const fileUploadTitleRecipe = cva(
  [
    'text-[var(--ds-file-upload-title)]',
    'group-data-[dragging]/fu:text-[var(--ds-file-upload-title-dragging)]',
    'group-data-[disabled]/fu:text-[var(--ds-file-upload-disabled)]',
  ],
  {
    variants: {
      layout: { card: 'text-body-md', row: 'text-body-md', tile: 'text-label-md' },
    },
    defaultVariants: { layout: 'card' },
  }
);

export const fileUploadDescriptionRecipe = cva([
  'text-body-sm text-[var(--ds-file-upload-description)]',
  'group-data-[disabled]/fu:text-[var(--ds-file-upload-disabled)]',
]);

/** Red rim, icon and text together, so the error never rests on colour alone. */
export const fileUploadErrorRecipe = cva([
  'inline-flex items-center gap-[4px] text-body-sm text-[var(--ds-file-upload-error)]',
]);

/** The native input. Visually hidden, out of the tab order: Browse (or the tile) is the control. */
export const fileUploadInputRecipe = cva(['sr-only']);

/**
 * One chosen file. The zone's fill and rim, solid, and the Row zone's padding, so an item that
 * replaces a Row zone keeps its footprint (both 70px). Every status is the same height.
 */
export const fileUploadItemRecipe = cva(
  [
    'ds-file-upload flex w-full items-center gap-[12px] rounded-md border px-[16px] py-[12px] font-sans',
    'bg-[var(--ds-file-upload-bg)] border-[var(--ds-file-upload-border)]',
  ],
  {
    variants: {
      status: {
        uploading: '',
        uploaded: '',
        failed: 'border-[var(--ds-file-upload-border-invalid)]',
      },
    },
    defaultVariants: { status: 'uploaded' },
  }
);

export const fileUploadItemPreviewRecipe = cva(['flex shrink-0']);

export const fileUploadItemBodyRecipe = cva(['flex min-w-0 flex-1 flex-col gap-[4px]']);

export const fileUploadItemNameRecipe = cva(['truncate text-body-md text-[var(--ds-file-upload-title)]']);

export const fileUploadItemMetaRecipe = cva(['flex min-w-0 items-center text-body-sm'], {
  variants: {
    status: {
      uploading: 'gap-[8px] text-[var(--ds-file-upload-description)]',
      uploaded: 'gap-[8px] text-[var(--ds-file-upload-description)]',
      failed: 'gap-[4px] text-[var(--ds-file-upload-error)]',
    },
  },
  defaultVariants: { status: 'uploaded' },
});

/** Fixed width and tabular figures, so the bar does not twitch as the number grows. */
export const fileUploadItemPercentRecipe = cva(['w-[32px] shrink-0 text-right tabular-nums']);

export const fileUploadItemActionsRecipe = cva(['flex shrink-0 items-center gap-[4px]']);

/**
 * The thumbnail. The hairline is drawn OVER the picture by a pseudo-element, because a border on the
 * box would sit under an image that covers it.
 */
export const filePreviewRecipe = cva(
  [
    'ds-file-upload relative flex shrink-0 items-center justify-center overflow-hidden rounded-sm bg-[var(--ds-file-upload-preview-bg)]',
  ],
  {
    variants: {
      type: {
        image:
          'h-[40px] w-[72px] after:absolute after:inset-0 after:rounded-[inherit] after:border after:border-[var(--ds-file-upload-preview-border)]',
        video:
          'h-[40px] w-[72px] after:absolute after:inset-0 after:rounded-[inherit] after:border after:border-[var(--ds-file-upload-preview-border)]',
        file: 'size-[40px] text-label-md text-[var(--ds-file-upload-preview-text)]',
      },
    },
    defaultVariants: { type: 'image' },
  }
);

export const filePreviewMediaRecipe = cva(['absolute inset-0 size-full object-cover']);

export const filePreviewPlayRecipe = cva([
  'relative flex size-[24px] items-center justify-center rounded-full',
  'bg-[var(--ds-file-upload-play-bg)] text-[var(--ds-file-upload-play-fg)]',
]);
